import { useEffect, useRef } from "react";

const isTypingTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.tagName === "SELECT"
  );
};

// A pause longer than this between two letters starts the word over, so "tech", a coffee, then
// "no" does not count.
const MAX_GAP_MS = 2000;

/**
 * Calls onTyped whenever the user types `word` on the page, outside any text field, so the
 * search box or a comment containing the word never fires it. Case-insensitive, any key with
 * Ctrl, Cmd or Alt held breaks the sequence, and so does a pause of more than MAX_GAP_MS
 * between letters.
 */
export const useTypedWord = (word: string, onTyped: () => void) => {
  const buffer = useRef("");
  const lastKeyAt = useRef(0);
  const callback = useRef(onTyped);

  useEffect(() => {
    callback.current = onTyped;
  });

  useEffect(() => {
    const target = word.toLowerCase();

    const onKeyDown = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target) || e.ctrlKey || e.metaKey || e.altKey) {
        buffer.current = "";
        return;
      }
      if (e.key.length !== 1) return;

      if (e.timeStamp - lastKeyAt.current > MAX_GAP_MS) buffer.current = "";
      lastKeyAt.current = e.timeStamp;

      buffer.current = (buffer.current + e.key.toLowerCase()).slice(
        -target.length
      );
      if (buffer.current === target) {
        buffer.current = "";
        callback.current();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [word]);
};
